import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import { Button, Form } from "react-bootstrap";
import { assetColumnSchema, getAssetColumnSchema } from "../../yup";
import styles from "./index.module.css";
import {
  createAssetColumn,
  getAssetColumns,
  getAssetZohoLabels,
  updateAssetColumn,
} from "../../service/api";
import { showNotification } from "../../service/showNotification";
import { convertFormula, revertFormula } from "../../utils/assetColumnFormula";
import { ButtonLoader } from "../ButtonLoader";
import CreatableSelect from "react-select/creatable";

const dataTypeOptions = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
];

const colId = "";

const ColumnModal = () => {
  const [zohoAssetOptions, setZohoAssetOptions] = useState([]);
  const [zohoAssetLoader, setZohoAssetLoader] = useState(true);
  const [formulaOptions, setFormulaOptions] = useState([]);

  // const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const [submitLoader, setSubmitLoader] = useState(false);

  const {
    reset,
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(assetColumnSchema),
    defaultValues: {
      dataType: dataTypeOptions[0].value,
      defaultValue: undefined,
      validation: {
        required: false,
      },
    },
  });

  console.log("errors", errors, watch());

  const isStringDataType = watch("dataType") === "string";
  const isNumDataType = watch("dataType") === "number";
  const selectedZohoLabels = watch("zohoLabel");
  const selectedDataType = watch("dataType");
  const isFormula = watch("isFormula") ?? false;
  const selectedCsvLabels = watch("csvLabel") || [];
  const isValidationRequired = watch("validation.required") ?? false;
  const formula = watch("formula");
  const showValidationToggle = isStringDataType || isNumDataType;
  const showFormulaToggle = !selectedCsvLabels.length;

  //formula field
  const handleFormulaChange = (e) => {
    const value = e.target.value;
    // setInputValue(value);
    setValue("formula", value);

    const cursor = e.target.selectionStart;
    const hashIndex = value.lastIndexOf("#", cursor - 1);
    const afterHash = value.slice(hashIndex + 1, cursor);

    if (hashIndex !== -1 && !/\s/.test(afterHash)) {
      const matches = formulaOptions.filter((opt) =>
        opt.label.toLowerCase().includes(afterHash.toLowerCase())
      );
      setFilteredOptions(matches);
      setShowDropdown(true);

      const rect = e.target.getBoundingClientRect();
    } else {
      setShowDropdown(false);
    }
  };

  //formula field
  const handleSelect = (option) => {
    const cursor = inputRef.current.selectionStart;
    const hashIndex = formula.lastIndexOf("#", cursor - 1);
    const before = formula.slice(0, hashIndex);
    const after = formula.slice(cursor);
    const newValue = `${before}#${option.label} ${after}`;

    // setInputValue(newValue);
    setValue("formula", newValue);
    setShowDropdown(false);
    inputRef.current.focus();
  };

  //fetch zoho labels
  const fetchAssetZohoLabels = async () => {
    try {
      const res = await getAssetZohoLabels();

      const options =
        res?.data?.map((item) => ({
          label: item?.label,
          value: item?.api_name,
        })) || [];
      setZohoAssetOptions(options);
    } catch (err) {
      console.log("err", err);
      showNotification("error", "Failed to fetch asset csv labels.");
    } finally {
      setZohoAssetLoader(false);
    }
  };

  //fetch asset column
  const fetchColumns = async () => {
    try {
      const res = await getAssetColumns();

      const options =
        res?.data?.map((item) => ({
          label: item?.label.replaceAll(" ", "_"),
          value: item?._id,
        })) || [];
      setFormulaOptions(options);

      const col = res?.data?.find((item) => colId === item._id);
      console.log("col", col);

      if (col) {
        reset({
          label: col.label,
          csvLabel: col.csvLabel,
          zohoLabel: col.zohoLabel,
          dataType: col.dataType,
          validation: col.validation,
          defaultValue: col.defaultValue,
          isFormula: col.isFormula,
          formula: col.isFormula
            ? revertFormula(col?.formula, options)
            : undefined,
        });
      }
    } catch (err) {
      console.log("err", err);
      showNotification("error", "Failed to fetch columns.");
    }
  };

  //form submit
  const onSubmit = async (values) => {
    const formulaObj = convertFormula(values?.formula, formulaOptions);
    if (formulaObj) {
      values.formula = formulaObj?.convertedFormula;
      values.dependencies = formulaObj?.dependencies;
    }
    setSubmitLoader(true);
    try {
      const res = colId
        ? await updateAssetColumn(colId, values)
        : await createAssetColumn(values);
      showNotification(
        "success",
        res?.message ||
          (colId ? "Asset updated successfully." : "New asset column created.")
      );
    } catch (err) {
      console.log("err", err);
      showNotification(
        "error",
        err?.response?.data?.message ||
          `Failed to ${colId ? "update" : "create"} new column.`
      );
    } finally {
      setSubmitLoader(false);
    }
  };

  useEffect(() => {
    fetchAssetZohoLabels();
    fetchColumns();
  }, []);

  useEffect(() => {
    setValue("isFormula", false);
  }, [showFormulaToggle]);

  useEffect(() => {
    //for formula field
    const handler = (e) => {
      if (!inputRef.current?.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
      <div className={styles.inputContainer}>
        <label className={styles.inputLabel}>Column Name</label>
        <input
          {...register("label")}
          className={styles.inputBox}
          placeholder="Enter column name"
        />
        {errors?.label && (
          <p className={styles.error}>{errors?.label?.message}</p>
        )}
      </div>
      <div className={styles.inputContainer}>
        <label className={styles.inputLabel}>Zoho Label</label>
        <Select
          isLoading={zohoAssetLoader}
          options={zohoAssetOptions}
          value={zohoAssetOptions.find(
            (item) => item.value === selectedZohoLabels
          )}
          onChange={(selected) =>
            setValue("zohoLabel", selected?.value, { shouldValidate: true })
          }
          menuPlacement="auto"
          styles={{ menuList: (base) => ({ ...base, maxHeight: 160 }) }}
        />
        {errors.zohoLabel && (
          <p className={styles.error}>{errors.zohoLabel.message}</p>
        )}
      </div>
      <div className={styles.inputContainer}>
        <label className={styles.inputLabel}>CSV Label</label>
        <CreatableSelect
          isMulti
          options={[]}
          value={selectedCsvLabels.map((label) => ({
            label, // Displayed text
            value: label, // Underlying value
          }))}
          onChange={(selected) => {
            const values = selected ? selected.map((item) => item.value) : [];
            setValue("csvLabel", values, { shouldValidate: true });
          }}
          onCreateOption={(inputValue) => {
            const cleanedValue = inputValue.trim();
            if (cleanedValue && !selectedCsvLabels.includes(cleanedValue)) {
              const newLabels = [...selectedCsvLabels, cleanedValue];
              setValue("csvLabel", newLabels, { shouldValidate: true });
            }
          }}
          placeholder="Type and select or create labels..."
          classNamePrefix="react-select"
          formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
          noOptionsMessage={() => "Enter CSV column for mapping."}
        />
        {errors?.csvLabel && (
          <p className={styles.error}>{errors?.csvLabel?.message}</p>
        )}
      </div>

      <div className={styles.inputContainer}>
        <label className={styles.inputLabel}>Data Type</label>
        <Select
          options={dataTypeOptions}
          value={dataTypeOptions.filter((opt) => selectedDataType == opt.value)}
          onChange={(selected) => {
            setValue("dataType", selected?.value, { shouldValidate: true });
          }}
          menuPlacement="auto"
          styles={{ menuList: (base) => ({ ...base, maxHeight: 160 }) }}
        />
        {errors?.dataType && (
          <p className={styles.error}>{errors?.dataType?.message}</p>
        )}
      </div>

      {showValidationToggle && (
        <div className={styles.addValidationContainer}>
          <label className={styles.inputLabel}>Add Validation</label>
          <Form.Check
            type="switch"
            checked={isValidationRequired}
            onChange={(e) => setValue("validation.required", e.target.checked)}
          />
        </div>
      )}

      {isValidationRequired && isStringDataType && (
        <div className={styles.dataTypeOptions}>
          <div className={styles.inputContainer}>
            <label>Min Length</label>
            <input
              type="number"
              {...register("validation.minLength")}
              className={styles.inputBox}
            />
            {errors?.validation?.minLength && (
              <p className={styles.error}>
                {errors?.validation?.minLength?.message}
              </p>
            )}
          </div>
          <div className={styles.inputContainer}>
            <label>Max Length</label>
            <input
              type="number"
              {...register("validation.maxLength")}
              className={styles.inputBox}
            />
            {errors?.validation?.maxLength && (
              <p className={styles.error}>
                {errors?.validation?.maxLength?.message}
              </p>
            )}
          </div>
        </div>
      )}

      {isValidationRequired && isNumDataType && (
        <div className={styles.dataTypeOptions}>
          <div className={styles.inputContainer}>
            <label>Min</label>
            <input
              type="number"
              {...register("validation.min")}
              className={styles.inputBox}
            />
            {errors?.validation?.min && (
              <p className={styles.error}>{errors?.validation?.min?.message}</p>
            )}
          </div>
          <div className={styles.inputContainer}>
            <label>Max</label>
            <input
              type="number"
              {...register("validation.max")}
              className={styles.inputBox}
            />
            {errors?.validation?.max && (
              <p className={styles.error}>{errors?.validation?.max?.message}</p>
            )}
          </div>
        </div>
      )}

      <div className={styles.inputContainer}>
        <label className={styles.inputLabel}>Default value</label>
        <input
          {...register("defaultValue")}
          className={styles.inputBox}
          placeholder="Enter default value for the column"
        />
        {errors?.defaultValue && (
          <p className={styles.error}>{errors?.defaultValue?.message}</p>
        )}
      </div>

      {showFormulaToggle && (
        <div className={styles.addValidationContainer}>
          <label className={styles.inputLabel}>Add Formula</label>
          <Form.Check
            type="switch"
            checked={isFormula}
            onChange={(e) => setValue("isFormula", e.target.checked)}
          />
        </div>
      )}

      {isFormula && (
        <div className={styles.inputContainer}>
          <div className={styles.wrapper}>
            <label className={styles.label}>Formula</label>
            <input
              type="text"
              {...register("formula")}
              className={styles.input}
              placeholder="Enter formula (type # to use column names)"
              value={formula}
              onChange={handleFormulaChange}
              ref={inputRef}
              autoComplete="off"
            />

            {showDropdown && (
              <div className={styles.dropdown}>
                {filteredOptions.length === 0 ? (
                  <div className={styles.option}>No matches</div>
                ) : (
                  filteredOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={styles.option}
                      onClick={() => handleSelect(opt)}
                    >
                      {opt.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <Button
        type="submit"
        isDisabled={submitLoader}
        className={styles.submitButton}
      >
        {submitLoader ? <ButtonLoader show={true} /> : "Submit"}
      </Button>
    </form>
  );
};

export default ColumnModal;
